import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, ShieldAlert, Loader2, AlertTriangle } from "lucide-react";

export default function AdminRejectedQRs() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (search) params.set("search", search);
      const { data } = await API.get(`/admin/rejected-qrs?${params}`);
      setRows(data.rejected || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-rejected-qrs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-red-600" /></div>
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Rejected QR Scans ({total})</h2>
            <p className="text-xs text-slate-500">QR scan attempts that failed both local registry + ERP market validation.</p>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchDraft); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="rejected-search-input"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search by mobile, user, QR string, product code…"
            className="h-10 pl-9 rounded-xl"
          />
        </div>
        <button type="submit" className="h-10 px-4 rounded-xl bg-[#008A99] text-white text-sm font-semibold hover:bg-[#006670]" data-testid="rejected-search-btn">Search</button>
        {search && (
          <button type="button" onClick={() => { setSearchDraft(""); setSearch(""); setPage(1); }} className="h-10 px-4 rounded-xl border text-sm font-semibold text-slate-600">Clear</button>
        )}
      </form>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm" data-testid="rejected-empty">
            <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-slate-200" />
            No rejected QR scans yet — every scan has been valid 🎉
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">QR String</th>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`rejected-row-${i}`}>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.attempted_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800">{r.user_name || "—"}</p>
                      <p className="text-xs text-slate-400">{r.user_mobile || "—"}</p>
                    </td>
                    <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700 break-all">{r.qr_string}</code></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.parsed_product_code || "—"}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" />{r.reason}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 text-sm">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1.5 rounded-lg border disabled:opacity-30" data-testid="rejected-prev-page">Previous</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1.5 rounded-lg border disabled:opacity-30" data-testid="rejected-next-page">Next</button>
        </div>
      )}
    </div>
  );
}
